/**
 * Start dialog „Co Cię do nas sprowadza?” (helpers/StartDialog.php,
 * views/new-mobile-map/_start_dialog.php). The server renders the whole
 * dialog hidden; this module decides whether it opens by itself, shows and
 * hides its parts, gives the parcel search of „Znajdź działkę” its
 * suggestions (guided-check/parcel-search.js, as on the find page) and fills
 * the price tile from podglad.json. Every sentence comes from the markup or
 * its data-* attributes.
 *
 * Only the browser knows the visitor's earlier choices, so it decides: the
 * dialog opens by itself when the page carries the flag
 * (data-start-dialog-auto="1"), the address has no parameter except campaign
 * ones, the visitor did not choose „Nie pokazuj…”, did not close it in the
 * last REOPEN_AFTER_MS (in any tab), has no unseen „Co nowego” and is not
 * shown „Dokończ sprawdzanie” of a check begun in this browser.
 * `?kreator=<situation>` always opens it (the server has already selected the
 * situation). [data-start-dialog-open] reopens it.
 */
import { openModal, showToast } from '../../shared/dialog';
import { initListingAutocomplete } from '../../shared/listing-autocomplete';
import { initParcelSearch } from '../../guided-check/parcel-search';
import { initStartDialogListings } from './start-dialog-listings';

var HIDE_KEY = 'rcn-start-dialog';
var HIDE_VALUE = 'hidden';
// When the dialog was last closed or left through a link (ms since 1970),
// shared by every tab of the browser.
var CLOSED_KEY = 'rcn-start-dialog-closed-at';
// A later visit: half a day after the dialog was closed it may open again
// (also „Dokończ sprawdzanie”, modules/guided-resume.js).
export var REOPEN_AFTER_MS = 12 * 60 * 60 * 1000;
var HINT_KEY = 'rcn-start-dialog-hint';
// Written and removed at once: whether the storage keeps what the dialog writes.
var PROBE_KEY = 'rcn-start-dialog-probe';
var PARAM_KREATOR = 'kreator';
// Below this width the preview stands under the situations (_start-dialog.scss).
var STACKED_QUERY = '(max-width: 760px)';
var HINT_TIMEOUT_MS = 9000;
// The place field of the statistics preview (the parcel search has its own, parcel-search.js).
var PLACE_TYPES = 'wojewodztwo,powiat,gmina,locality';
// The search ranks villages of the same name above the city they share it with,
// so a wider page is asked for and arrangePlaces() keeps the list short.
var PLACE_REQUEST_LIMIT = 20;
var PLACE_LIMIT = 8;
var PLACE_MIN_LENGTH = 2;
// Pixels kept free between an open list of suggestions and the visible
// bottom of the dialog body or the top of a phone's keyboard.
var LIST_MARGIN = 12;
// The least room a list gets, about two options; with less room under the
// field on a stacked screen the field first moves up (LIST_LIFT_ROOM).
var LIST_MIN_ROOM = 112;
var LIST_LIFT_ROOM = 160;
// Pixels kept between the situation the dialog opens on and the edge of the
// body, so its focus ring shows whole.
var CHOICE_MARGIN = 8;
// How long after a focus change the dialog checks that the browser's scroll
// left the focused control of a preview in view (revealInPreview).
var REVEAL_MS = 400;
// Campaign and click identifiers that arrive with a plain visit of the homepage
// (Google Ads adds gad_source and gad_campaignid next to gclid).
var TRACKING_PARAM = /^(utm_[a-z0-9_]+|gad_[a-z0-9_]+|fbclid|gclid|gbraid|wbraid|dclid|srsltid|msclkid|yclid|igshid|ttclid|twclid|li_fat_id|epik|mc_cid|mc_eid|_hsenc|_hsmi|mkt_tok|_ga|_gl)$/i;

function store(kind) {
    try {
        return window[kind] || null;
    } catch (e) {
        return null;
    }
}

function readStored(kind, key) {
    var target = store(kind);
    if (!target) return null;
    try {
        return target.getItem(key);
    } catch (e) {
        return null;
    }
}

function writeStored(kind, key, value) {
    var target = store(kind);
    if (!target) return;
    try {
        if (value === null) target.removeItem(key);
        else target.setItem(key, value);
    } catch (e) { /* storage blocked: the choice lasts for this page only */ }
}

/** Storage the dialog can write to: without it the dialog could never remember being closed. */
function writableStorage() {
    var target = store('localStorage');
    if (!target) return false;
    try {
        target.setItem(PROBE_KEY, '1');
        var kept = target.getItem(PROBE_KEY) === '1';
        target.removeItem(PROBE_KEY);
        return kept;
    } catch (e) {
        return false;
    }
}

/** True when the address only carries campaign parameters (or none). */
export function isPlainVisit(params) {
    var plain = true;
    params.forEach(function(value, name) {
        if (!TRACKING_PARAM.test(name)) plain = false;
    });
    return plain;
}

/**
 * Whether the dialog opens without being asked: only with the flag, on a plain
 * visit, without unseen news of a returning visitor and without „Dokończ
 * sprawdzanie” over the map, when the visitor has not hidden it and has not
 * closed it within REOPEN_AFTER_MS of `now`. When the storage cannot be
 * written the answer is no: an unknown visitor, or one whose closing and
 * „Nie pokazuj…” cannot be saved, would otherwise see it on every page view.
 */
export function opensByItself(options) {
    if (!options.auto || !isPlainVisit(options.params) || options.newsPending || options.resumeShown) return false;
    if (!writableStorage()) return false;
    if (readStored('localStorage', HIDE_KEY) === HIDE_VALUE) return false;
    var closedAt = Number(readStored('localStorage', CLOSED_KEY));
    return !(closedAt > 0 && options.now - closedAt < REOPEN_AFTER_MS);
}

/**
 * The area a place suggestion stands for: województwa, powiaty and gminy by
 * their own code, a locality by the code of its gmina. podglad.json turns a
 * city powiat and a part of a gmina into the canonical gmina page.
 */
export function areaForPlace(item) {
    var code = String((item && item.teryt_gmi) || '');
    var type = item && item.type;
    if (type === 'wojewodztwo' && /^\d{2}$/.test(code)) return { level: 'wojewodztwo', code: code };
    if (type === 'powiat' && /^\d{4}$/.test(code)) return { level: 'powiat', code: code };
    if (type === 'gmina' && /^\d{7}$/.test(code)) return { level: 'gmina', code: code };
    if (type === 'locality' && /^\d{7}$/.test(code)) return { level: 'gmina', code: code, locality: String(item.nazwa || '') };
    return null;
}

/**
 * The report an area resolves to on the server: a city powiat (code 61 and
 * up) is its gmina, as in AreaPricePreviewService::normalizeArea().
 */
function reportKey(area) {
    if (area.level === 'powiat' && parseInt(area.code.slice(2), 10) >= 61) return 'gmina:' + area.code + '011';
    return area.level + ':' + area.code;
}

/** Lower case without Polish diacritics: „Zabór” and „zabor” compare equal. */
function fold(text) {
    return String(text || '').toLocaleLowerCase('pl').replace(/ł/g, 'l').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}

/**
 * Suggestions in the order the dialog needs. The search ranks six villages
 * called Zielona Góra above the city and fuzzy matches („Myszków”) above an
 * exact „Droszków”, so: exact names first, then names that start with the
 * query, then the rest; within each group województwa, powiaty and gminy
 * before localities. A locality or a city powiat that leads to the same report
 * as a listed gmina of the same name is dropped. At most PLACE_LIMIT items.
 */
export function arrangePlaces(items, query) {
    var wanted = fold(query);
    var areaNames = {};
    var entries = [];
    (items || []).forEach(function(item, index) {
        var area = areaForPlace(item);
        if (!area) return;
        var name = fold(item.nazwa).replace(/^powiat\s+/, '');
        var key = reportKey(area);
        var locality = area.locality !== undefined;
        if (!locality) {
            if (areaNames[key] !== undefined) return;
            areaNames[key] = name;
        }
        var tier = name === wanted ? 0 : (wanted !== '' && name.indexOf(wanted) === 0 ? 1 : 2);
        entries.push({ item: item, key: key, name: name, locality: locality, tier: tier, index: index });
    });
    return entries
        .filter(function(entry) { return !(entry.locality && areaNames[entry.key] === entry.name); })
        .sort(function(a, b) {
            return a.tier - b.tier || (a.locality ? 1 : 0) - (b.locality ? 1 : 0) || a.index - b.index;
        })
        .slice(0, PLACE_LIMIT)
        .map(function(entry) { return entry.item; });
}

function sameName(a, b) {
    var fold = function(text) { return String(text || '').trim().toLocaleLowerCase('pl'); };
    return fold(a) === fold(b);
}

/**
 * Where the focus goes when the dialog or a card over the map closes and its
 * own control is gone: the visible „Sprawdź krok po kroku”, or on a phone the
 * menu that holds it, so a keyboard user knows how to come back.
 */
export function visibleStartOpener(doc) {
    var openers = doc.querySelectorAll('[data-start-dialog-open]');
    for (var i = 0; i < openers.length; i++) {
        if (openers[i].offsetParent !== null) return openers[i];
    }
    var menu = doc.getElementById('nm-top-nav-btn');
    return menu && menu.offsetParent !== null ? menu : null;
}

export function initStartDialog(options) {
    var params = (options && options.params) || new URLSearchParams(window.location.search || '');
    var root = document.getElementById('nm-start-dialog');
    if (!root) return { autoOpened: false };

    var texts = {};
    try {
        texts = JSON.parse(root.getAttribute('data-start-dialog-texts') || '{}') || {};
    } catch (e) { /* defaults below */ }

    var status = root.querySelector('[data-start-status]');
    var dontShow = root.querySelector('[data-start-dont-show]');
    var placeInput = root.querySelector('[data-start-place]');
    var placeList = root.querySelector('[data-start-place-list]');
    var placeHint = root.querySelector('[data-start-place-hint]');
    var result = root.querySelector('[data-start-result]');
    var metric = root.querySelector('[data-start-metric]');
    var medianLabel = root.querySelector('[data-start-median-label]');
    // The label of the markup, for an answer that names no label of its own.
    var defaultMedianLabel = medianLabel ? medianLabel.textContent.trim() : '';
    var median = root.querySelector('[data-start-median]');
    var unit = root.querySelector('[data-start-unit]');
    var windowLabel = root.querySelector('[data-start-window]');
    var help = root.querySelector('[data-start-help]');
    var lowSample = root.querySelector('[data-start-low]');
    var resultNote = root.querySelector('[data-start-result-note]');
    var statCta = root.querySelector('[data-start-stat-cta]');
    var previewUrl = root.getAttribute('data-start-dialog-preview-url') || '';

    var isOpen = false;
    var openedWithoutClick = false;
    var preview = null;
    var previewRevision = 0;
    var placeLabel = '';
    var localityName = '';
    var typeChosenByVisitor = false;
    var typeHint = '';
    // A podglad.json request is on its way (a cold large area takes seconds).
    var previewLoading = false;

    if (dontShow) dontShow.checked = readStored('localStorage', HIDE_KEY) === HIDE_VALUE;

    function checked(selector) {
        var inputs = root.querySelectorAll(selector);
        for (var i = 0; i < inputs.length; i++) {
            if (inputs[i].checked) return inputs[i];
        }
        return inputs[0] || null;
    }

    function situation() {
        var input = checked('[data-start-situation]');
        return input ? input.value : '';
    }

    function statType() {
        var input = checked('[data-start-type]');
        return input ? input.value : '';
    }

    function setStatType(type) {
        root.querySelectorAll('[data-start-type]').forEach(function(input) {
            input.checked = input.value === type;
        });
    }

    function announce(text) {
        if (status) status.textContent = text || '';
    }

    function visibleTitle(container) {
        var titles = container ? container.querySelectorAll('[data-start-panel-title]') : [];
        for (var i = 0; i < titles.length; i++) {
            if (!titles[i].closest('[hidden]')) return titles[i].textContent.trim();
        }
        return '';
    }

    function matches(query) {
        return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
    }

    // On a narrow screen the preview is under the list: a tap on a situation,
    // also on the one already chosen, brings it into view; keys leave the
    // scroll alone. A pointer choice of the way to find a parcel moves on to
    // its field. A key pressed after a tap that changed nothing (the way
    // already chosen) is a key choice again.
    var tappedSituation = false;
    var methodByPointer = false;
    root.addEventListener('pointerdown', function(event) {
        var target = event.target;
        var choice = target.closest ? target.closest('label') : null;
        tappedSituation = Boolean(choice && choice.querySelector('[data-start-situation]'));
        methodByPointer = Boolean(target.closest && target.closest('[data-start-parcel-methods]'));
    });
    root.addEventListener('keydown', function() {
        tappedSituation = false;
        methodByPointer = false;
    });

    function revealPanel(panel) {
        if (!tappedSituation || !panel || !matches(STACKED_QUERY) || typeof panel.scrollIntoView !== 'function') return;
        panel.scrollIntoView({ block: 'start', behavior: matches('(prefers-reduced-motion: reduce)') ? 'auto' : 'smooth' });
    }

    var shownPanel = null;

    function syncPanels(shouldAnnounce) {
        var id = situation();
        var active = null;
        root.querySelectorAll('[data-start-panel]').forEach(function(panel) {
            var current = panel.getAttribute('data-start-panel') === id;
            panel.hidden = !current;
            if (current) active = panel;
        });
        shownPanel = active;
        if (shouldAnnounce) {
            announce(visibleTitle(active));
            revealPanel(active);
        }
        tappedSituation = false;
    }

    // The scrolling box that clips a list of suggestions: the dialog body, or
    // below 560px of height (`ms-modal--scroll-short`) the whole dialog.
    function clipBox(node) {
        if (typeof window.getComputedStyle !== 'function') return null;
        for (var box = node && node.parentElement; box && box !== root; box = box.parentElement) {
            if (window.getComputedStyle(box).overflowY !== 'visible') return box;
        }
        return null;
    }

    // The visible edges of a field's box: the body, the dialog and the visual
    // viewport, which a phone's keyboard shortens and may move down the
    // screen (offsetTop, in the coordinates of getBoundingClientRect).
    function visibleEdges(box) {
        var edges = { top: 0, bottom: window.innerHeight || Infinity };
        [box, root.querySelector('[role="dialog"]')].forEach(function(edge) {
            if (!edge) return;
            var rect = edge.getBoundingClientRect();
            edges.top = Math.max(edges.top, rect.top);
            edges.bottom = Math.min(edges.bottom, rect.bottom);
        });
        var viewport = window.visualViewport;
        if (viewport) {
            edges.top = Math.max(edges.top, viewport.offsetTop);
            edges.bottom = Math.min(edges.bottom, viewport.offsetTop + viewport.height);
        }
        return edges;
    }

    // Room for a list under and above its field, less the 4px gap between
    // them (_feedback.scss) and LIST_MARGIN.
    function roomAround(input, box) {
        var edges = visibleEdges(box);
        var rect = input.getBoundingClientRect();
        return { below: edges.bottom - rect.bottom - 4 - LIST_MARGIN, above: rect.top - edges.top - 4 - LIST_MARGIN };
    }

    // The lists float over the preview: each one may reach the visible edge
    // and scrolls inside itself beyond it (--nm-start-dialog-list-room,
    // _start-dialog.scss). With `lift`, a field of a stacked screen left with
    // less than LIST_LIFT_ROOM under it first scrolls to the top of what is
    // visible of its box, label included; a field the box cannot lift so far
    // (the last one of a short preview) opens its list above itself when
    // there is more room there (`is-above` of the shared combobox, on the
    // element that holds the field and its list, as listing-autocomplete.js
    // finds it).
    function fitList(input, lift) {
        if (!input || typeof input.getBoundingClientRect !== 'function' || !root.style || typeof root.style.setProperty !== 'function') return;
        var box = clipBox(input);
        var room = roomAround(input, box);
        if (lift && box && room.below < LIST_LIFT_ROOM && matches(STACKED_QUERY)) {
            var label = (input.labels && input.labels[0]) || input;
            box.scrollTop += label.getBoundingClientRect().top - visibleEdges(box).top - LIST_MARGIN;
            room = roomAround(input, box);
        }
        var above = room.below < LIST_LIFT_ROOM && room.above > room.below;
        var list = listOf(input);
        if (list && list.parentElement) list.parentElement.classList.toggle('is-above', above);
        root.style.setProperty('--nm-start-dialog-list-room', Math.max(LIST_MIN_ROOM, Math.floor(above ? room.above : room.below)) + 'px');
    }

    // The choice of the situation the dialog opens on, whole in view in the
    // body, or with `atTop` at the top of it. Only the body scrolls:
    // scrollIntoView() would scroll the dialog too and hide its title and
    // close button.
    function showChoice(choice, atTop) {
        var box = typeof choice.getBoundingClientRect === 'function' ? clipBox(choice) : null;
        if (!box) return;
        var edges = visibleEdges(box);
        var rect = choice.getBoundingClientRect();
        if (atTop || rect.top < edges.top + CHOICE_MARGIN) box.scrollTop += rect.top - edges.top - CHOICE_MARGIN;
        else if (rect.bottom > edges.bottom - CHOICE_MARGIN) box.scrollTop += rect.bottom - edges.bottom + CHOICE_MARGIN;
    }

    function isSuggestField(node) {
        return Boolean(node && node.matches && node.matches('[role="combobox"]') && root.contains(node));
    }

    function nextFrame(fn) {
        return typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(fn) : window.setTimeout(fn, 16);
    }

    // Scrolling the body, turning the phone, opening its keyboard or moving
    // the screen under it moves the field: the room of its list follows, once
    // per frame.
    var fitFrame = 0;
    function refit(lift) {
        var active = document.activeElement;
        if (!isOpen || fitFrame || !isSuggestField(active)) return;
        fitFrame = nextFrame(function() {
            fitFrame = 0;
            fitList(active, lift);
        });
    }

    // Beside the list a preview taller than the body stays at its top until
    // the list has scrolled past its foot (sticky, _start-dialog.scss). A
    // browser that brings a focused control at that foot into view counts on
    // the preview moving with the body and may stop short of it; the body
    // then scrolls to its end, where the foot of the preview is in view. The
    // control is checked in the next frame, and after every scroll of the
    // next REVEAL_MS in case a browser scrolls later than the focus.
    var reveal = null;
    function checkReveal(first) {
        if (!reveal) return;
        if ((!first && Date.now() > reveal.until) || document.activeElement !== reveal.control) {
            reveal = null;
        } else if (reveal.control.getBoundingClientRect().bottom > visibleEdges(reveal.box).bottom) {
            reveal.box.scrollTop = reveal.box.scrollHeight;
        }
    }

    function revealInPreview(control) {
        var panel = control.closest ? control.closest('[data-start-panel]') : null;
        var box = panel && typeof control.getBoundingClientRect === 'function' && !matches(STACKED_QUERY) ? clipBox(panel) : null;
        reveal = box ? { control: control, box: box, until: Date.now() + REVEAL_MS } : null;
        if (reveal) nextFrame(function() { checkReveal(true); });
    }

    root.addEventListener('focusin', function(event) {
        if (isSuggestField(event.target)) fitList(event.target, false);
        else revealInPreview(event.target);
    });
    root.addEventListener('input', function(event) {
        if (isSuggestField(event.target)) fitList(event.target, true);
    });
    root.addEventListener('scroll', function() {
        refit(false);
        checkReveal(false);
    }, true);
    if (typeof window.addEventListener === 'function') window.addEventListener('resize', function() { refit(true); });
    if (window.visualViewport && typeof window.visualViewport.addEventListener === 'function') {
        window.visualViewport.addEventListener('resize', function() { refit(true); });
        window.visualViewport.addEventListener('scroll', function() { refit(false); });
    }

    // The list of suggestions of a field (its `aria-controls`).
    function listOf(input) {
        var id = input.getAttribute('aria-controls') || '';
        return id ? root.querySelector('#' + id) : null;
    }

    // „Kupuję działkę”: the search of „Znajdź działkę” in the preview, the
    // find page's GET forms sent to its results and confirmation. The switch
    // shows the form of the chosen way; the place and address fields get the
    // find page's suggestions (guided-check/parcel-search.js).
    function syncParcelMethod(focusField) {
        var input = checked('[data-start-parcel-method]');
        var method = input ? input.value : '';
        root.querySelectorAll('[data-start-parcel-form]').forEach(function(form) {
            var shown = form.getAttribute('data-start-parcel-form') === method;
            form.hidden = !shown;
            var field = shown && focusField ? form.querySelector('input:not([type="hidden"])') : null;
            if (field && typeof field.focus === 'function') field.focus();
        });
    }

    // A form the script sends, as after a chosen address; form.submit() fires
    // no submit event, so it marks the dialog closed itself.
    function submitNow(form) {
        if (!form) return;
        if (typeof form.requestSubmit === 'function') {
            form.requestSubmit();
        } else {
            markClosed();
            form.submit();
        }
    }

    // A chosen address sends its form at once, which leaves the dialog.
    var parcelAddress = root.querySelector('[data-start-parcel-address]');
    initParcelSearch({
        placeInput: root.querySelector('[data-start-parcel-place]'),
        placeKey: root.querySelector('[data-start-parcel-place-key]'),
        addressInput: parcelAddress,
        addressId: root.querySelector('[data-start-parcel-address-id]'),
        addressForm: parcelAddress ? parcelAddress.closest('form') : null,
        placesUrl: root.getAttribute('data-start-dialog-parcel-places-url') || '',
        addressesUrl: root.getAttribute('data-start-dialog-parcel-addresses-url') || '',
        texts: texts,
        submit: submitNow,
    });

    var listings = initStartDialogListings(root, { checked: checked, announce: announce, visibleTitle: visibleTitle });

    function rememberChoice() {
        writeStored('localStorage', HIDE_KEY, dontShow && dontShow.checked ? HIDE_VALUE : null);
    }

    // Closed now: not again by itself for REOPEN_AFTER_MS, in this tab or another.
    function markClosed() {
        writeStored('localStorage', CLOSED_KEY, String(Date.now()));
        rememberChoice();
    }

    function setNote(node, text) {
        if (!node) return;
        node.textContent = text || '';
        node.hidden = !text;
    }

    function placeHints(entry) {
        if (previewLoading) return texts.loading || '';
        var hints = [];
        if (preview && localityName && !sameName(localityName, preview.name) && texts.localityHint) {
            hints.push(texts.localityHint.replace('{place}', localityName).replace('{locative}', preview.locative));
        }
        if (typeHint) hints.push(typeHint);
        if (entry && !entry.available && entry.note) hints.push(entry.note);
        return hints.join(' ');
    }

    function renderStatistics(shouldAnnounce) {
        var entry = preview && preview.types ? preview.types[statType()] || null : null;
        if (statCta) {
            statCta.textContent = entry ? entry.cta : (texts.defaultCta || statCta.textContent);
            statCta.setAttribute('href', entry ? entry.url : (statCta.getAttribute('data-default-href') || statCta.getAttribute('href')));
        }
        setNote(placeHint, placeHints(entry));
        var shown = !!(entry && entry.available);
        if (result) result.hidden = !shown;
        if (shown) {
            var hasMedian = entry.median !== null && entry.median !== undefined;
            // Houses are priced per square metre of the usable area: the label of the type says so, and names the „?”.
            var label = entry.medianLabel || defaultMedianLabel;
            if (metric) metric.classList.toggle('ms-metric--empty', !hasMedian);
            if (medianLabel) medianLabel.textContent = label;
            if (median) median.textContent = hasMedian ? entry.median : (texts.noData || '');
            if (unit) unit.textContent = hasMedian ? (entry.unit || '') : '';
            if (windowLabel) windowLabel.textContent = [entry.window, entry.deedsLabel].filter(Boolean).join(' · ');
            if (help) {
                help.setAttribute('data-info-popup', entry.help || '');
                if (texts.helpLabel && label) help.setAttribute('aria-label', texts.helpLabel.replace('{label}', function() { return label; }));
            }
            if (lowSample) {
                lowSample.hidden = !entry.lowSample;
                lowSample.setAttribute('data-info-popup', entry.lowSampleNote || '');
            }
            setNote(resultNote, entry.note);
        }
        // The tile in words (median, window, deeds, sample) after the hints;
        // nothing while the prices of a place are still loading.
        if (shouldAnnounce && !previewLoading) {
            announce([
                placeHint && !placeHint.hidden ? placeHint.textContent : '',
                shown ? entry.summary || '' : '',
            ].filter(Boolean).join(' '));
        }
    }

    function resetPreview() {
        previewRevision++;
        previewLoading = false;
        preview = null;
        placeLabel = '';
        localityName = '';
        typeHint = '';
        renderStatistics(false);
    }

    function loadPreview(area) {
        if (!previewUrl) return;
        var revision = ++previewRevision;
        preview = null;
        typeHint = '';
        localityName = area.locality || '';
        previewLoading = true;
        renderStatistics(false);
        announce(texts.loading || '');
        var url = previewUrl.replace('{poziom}', encodeURIComponent(area.level)).replace('{kod}', encodeURIComponent(area.code));
        window.fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function(response) {
                if (!response.ok) {
                    var failure = new Error('preview ' + response.status);
                    failure.status = response.status;
                    throw failure;
                }
                return response.json();
            })
            .then(function(data) {
                if (revision !== previewRevision) return;
                previewLoading = false;
                preview = data;
                if (!typeChosenByVisitor && data.preferredType) {
                    setStatType(data.preferredType);
                    typeHint = data.preferredNote || '';
                }
                renderStatistics(true);
            })
            .catch(function(failure) {
                if (revision !== previewRevision) return;
                // 404: the area exists in the search but has no transactions.
                var message = failure && failure.status === 404 ? texts.noArea : texts.error;
                previewLoading = false;
                preview = null;
                renderStatistics(false);
                setNote(placeHint, message || '');
                announce(message || '');
            });
    }

    // „miasto, województwo lubuskie”, „gmina, powiat krakowski”,
    // „miejscowość, gmina Zabór, powiat zielonogórski”: enough to tell apart
    // the places that share a name.
    function placeMeta(item) {
        var kind = {
            wojewodztwo: texts.kindVoivodeship,
            powiat: texts.kindPowiat,
            gmina: item.rodzaj === 'granice miasta' ? texts.kindCity : texts.kindGmina,
            locality: texts.kindLocality,
        }[item.type] || '';
        var voivodeship = item.wojewodztwo ? (texts.kindVoivodeship || '') + ' ' + item.wojewodztwo : '';
        // A city with powiat rights is its own powiat: the voivodeship says more.
        var powiat = item.powiat && !sameName(item.powiat, item.nazwa) && !sameName(item.powiat, item.gmina) ? item.powiat : voivodeship;
        var parts = [kind];
        if (item.type === 'locality') {
            if (item.gmina && !sameName(item.gmina, item.nazwa)) parts.push((texts.kindGmina || '') + ' ' + item.gmina);
            parts.push(powiat);
        } else if (item.type === 'gmina') {
            parts.push(powiat);
        } else if (item.type === 'powiat') {
            parts.push(voivodeship);
        }
        return parts.filter(Boolean).join(', ');
    }

    if (placeInput && placeList) {
        initListingAutocomplete({
            input: placeInput,
            results: placeList,
            url: root.getAttribute('data-start-dialog-search-url') || '',
            params: function(query) {
                return { q: query, limit: PLACE_REQUEST_LIMIT, types: PLACE_TYPES };
            },
            label: function(item) { return String(item.nazwa || ''); },
            meta: placeMeta,
            filter: function(item) { return areaForPlace(item) !== null; },
            arrange: arrangePlaces,
            onSelect: function(item) {
                placeInput.value = String(item.nazwa || '');
                placeLabel = placeInput.value;
                loadPreview(areaForPlace(item));
            },
            minLength: PLACE_MIN_LENGTH,
            loading: texts.searchLoading,
            empty: texts.searchEmpty,
            error: texts.searchError,
            optionClass: 'ms-menu__item',
            messageClass: 'ms-menu__message',
        });
        placeInput.addEventListener('input', function() {
            if (placeLabel && placeInput.value !== placeLabel) resetPreview();
        });
    }

    root.addEventListener('change', function(event) {
        var target = event.target;
        if (!target || !target.matches) return;
        if (target.matches('[data-start-situation]')) syncPanels(true);
        else if (target.matches('[data-start-parcel-method]')) {
            syncParcelMethod(methodByPointer);
            methodByPointer = false;
        } else if (target.matches('[data-start-type]')) {
            typeChosenByVisitor = true;
            typeHint = '';
            renderStatistics(true);
        } else if (target.matches('[data-start-dont-show]')) rememberChoice();
    });

    // Sending a search leaves the page, as following a call to action does.
    root.addEventListener('submit', function(event) {
        var form = event.target;
        if (form && form.matches && form.matches('[data-start-form]')) markClosed();
    });

    root.addEventListener('click', function(event) {
        // The radio of the situation already shown: a click on it changes
        // nothing, so only a tap brings its preview into view (revealPanel).
        var radio = event.target.matches && event.target.matches('[data-start-situation]') ? event.target : null;
        if (radio && shownPanel && radio.value === shownPanel.getAttribute('data-start-panel')) {
            revealPanel(shownPanel);
            tappedSituation = false;
            return;
        }
        var example = event.target.closest('[data-start-example]');
        if (example) {
            event.preventDefault();
            if (placeInput) {
                placeInput.value = example.textContent.trim();
                placeLabel = placeInput.value;
            }
            loadPreview({
                level: example.getAttribute('data-level'),
                code: example.getAttribute('data-code'),
            });
            return;
        }
        // Following a call to action closes the dialog by leaving the page.
        if (event.target.closest('[data-start-cta]')) markClosed();
    });

    // Once per browser, after a dialog that opened by itself: where to find it
    // again — the button above the map where it is shown, otherwise the menu.
    function showReopenHint() {
        if (!openedWithoutClick || readStored('localStorage', HINT_KEY) !== null) return;
        writeStored('localStorage', HINT_KEY, '1');
        var opener = visibleStartOpener(document);
        var onMap = Boolean(opener && opener.hasAttribute('data-start-dialog-open'));
        var text = root.getAttribute(onMap ? 'data-start-dialog-toast' : 'data-start-dialog-toast-menu');
        if (text) showToast(text, { actionLabel: root.getAttribute('data-start-dialog-toast-action') || '', timeout: HINT_TIMEOUT_MS });
    }

    function open(returnFocus, withoutClick) {
        if (isOpen) return;
        isOpen = true;
        openedWithoutClick = withoutClick;
        syncPanels(false);
        syncParcelMethod(false);
        listings.sync(false);
        var closed = openModal(root, { returnFocus: returnFocus || visibleStartOpener(document) });
        // A situation low in the list comes into view with its whole choice,
        // and the body scrolls no further than that (a browser centres a
        // focused field). Beside the list the preview stays at the top of the
        // body (sticky in _start-dialog.scss), so its title, switch and field
        // stay in view too. Stacked, the preview follows the list: a
        // situation other than the first (asked for by `?kreator`, or chosen
        // before the dialog was closed) goes to the top of the body, with the
        // beginning of its preview under it.
        var current = checked('[data-start-situation]');
        if (current && typeof current.focus === 'function') {
            current.focus({ preventScroll: true });
            var choice = current.closest('label');
            if (choice) showChoice(choice, matches(STACKED_QUERY) && root.querySelector('[data-start-situation]') !== current);
        }
        closed.then(function() {
            isOpen = false;
            markClosed();
            showReopenHint();
        });
    }

    document.addEventListener('click', function(event) {
        var trigger = event.target.closest && event.target.closest('[data-start-dialog-open]');
        if (!trigger) return;
        event.preventDefault();
        // The menu item is hidden with its menu before this runs: the focus then
        // returns to the visible opener (the menu button) instead.
        open(trigger.offsetParent !== null ? trigger : null, false);
    });

    syncPanels(false);
    // A reload may give back the radio of the address search: its form shows.
    syncParcelMethod(false);
    listings.sync(false);
    renderStatistics(false);

    var requested = params.get(PARAM_KREATOR);
    var explicit = requested !== null && requested.trim() !== '';
    var opening = explicit || opensByItself({
        auto: root.getAttribute('data-start-dialog-auto') === '1',
        params: params,
        newsPending: Boolean(options && options.newsPending),
        resumeShown: Boolean(options && options.resumeShown),
        now: Date.now(),
    });
    if (opening) open(null, true);

    return { autoOpened: opening };
}
